// The board tabs' pure decisions: each tab's words and constraint mark, which
// games still need their record read, and the constraint scope a tab's board
// is registered under. Kept out of the component so they are tested without
// React.
import type { ReviewTabs } from '@chess/api-review';
import { constraintScopeId, type ConstraintOwner, type ConstraintScope } from '@chess/api-constraints';
import type { BoardTabItem } from './BoardTabs.tsx';
import { CURRENT_GAME, tabLabel, tabTitle, type TabRecord } from './labels.ts';

/** The constraint scope of a review tab's board: each tab is its own. */
export const tabScope = (tabId: string): ConstraintScope => ({ kind: 'reviewTab', id: constraintScopeId(tabId) });

/**
 * The strip's tabs. The pinned tab always reads "Current game", its date and
 * players one hover away; an opened tab reads its date and its opener's
 * caption, empty while its record loads. A constrained tab carries its
 * owner's label and detail.
 */
export function itemsOf(tabs: ReviewTabs, records: Readonly<Record<string, TabRecord>>, owners: Readonly<Record<string, ConstraintOwner | undefined>>): BoardTabItem[] {
  return tabs.tabs.map((t) => {
    const record = t.gameId ? records[t.gameId] : undefined;
    const owner = owners[t.id];
    return {
      id: t.id,
      pinned: t.pinned,
      label: t.pinned ? CURRENT_GAME : record ? tabLabel(record, t.caption) : '',
      title: record ? tabTitle(record) : undefined,
      constraint: owner ? { label: owner.label, detail: owner.detail } : undefined,
    };
  });
}

/** The games whose record is still to read, each once. */
export function recordsToLoad(tabs: ReviewTabs, records: Readonly<Record<string, TabRecord>>): string[] {
  const out: string[] = [];
  for (const tab of tabs.tabs) {
    const id = tab.gameId;
    if (id && !records[id] && !out.includes(id)) out.push(id);
  }
  return out;
}
